import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

export interface ArticleData {
  title: string;
  seoTitle: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  imageUrl: string;
  imageAlt: string;
  readTime: string;
  category: string;
  sections: {
    heading: string;
    paragraphs: string[];
    bulletPoints?: string[];
    subSections?: {
      heading: string;
      paragraphs: string[];
    }[];
  }[];
  faqs: { q: string; a: string }[];
  ctaText: string;
}

export default function ArticleTemplate({ data }: { data: ArticleData }) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const siteUrl = import.meta.env.VITE_SITE_URL;

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: data.title,
          description: data.description,
          url: data.canonicalUrl,
          image: data.imageUrl,
          author: {
            '@type': 'Organization',
            name: 'Kakobuy Polska Guide',
            url: siteUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Kakobuy Polska Guide',
            url: siteUrl,
            logo: {
              '@type': 'ImageObject',
              url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
            },
          },
          datePublished: '2026-07-21',
          dateModified: '2026-07-21',
          inLanguage: 'pl',
        }}
      />
      <Navbar transparent={false} />

      <article className="pt-24 md:pt-28 pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <nav className="mb-6 md:mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Strona główna</Link>
              </li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li>
                <Link to="/poradnik-kakobuy" className="hover:text-primary-600 transition-colors cursor-pointer">Poradniki</Link>
              </li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-800 font-medium">{data.title}</li>
            </ol>
          </nav>

          <header className="mb-8 md:mb-12">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">{data.category}</span>
              <span className="text-xs text-foreground-400">{data.readTime} minut czytania</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {data.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              {data.description}
            </p>
          </header>

          <figure className="mb-8 md:mb-12">
            <img
              src={data.imageUrl}
              alt={data.imageAlt}
              className="w-full rounded-2xl"
            />
          </figure>

          <div className="prose-custom space-y-8 md:space-y-10">
            {data.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-3"
                    dangerouslySetInnerHTML={{ __html: p }}
                  />
                ))}
                {section.bulletPoints && (
                  <ul className="space-y-2.5 mb-4">
                    {section.bulletPoints.map((item, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.subSections && section.subSections.map((sub, sIdx) => (
                  <div key={sIdx} className="mb-3">
                    <h3 className="font-semibold text-foreground-900 text-base mb-1.5">{sub.heading}</h3>
                    {sub.paragraphs.map((sp, spIdx) => (
                      <p key={spIdx} className="text-foreground-600 text-sm leading-relaxed mb-2"
                        dangerouslySetInnerHTML={{ __html: sp }}
                      />
                    ))}
                  </div>
                ))}
              </section>
            ))}

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Najczęściej Zadawane Pytania
              </h2>
              <div className="space-y-3">
                {data.faqs.map((faq, fIdx) => (
                  <div key={fIdx} className="p-4 bg-background-50 rounded-xl border border-background-200">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1.5">{faq.q}</h4>
                    <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-primary-50 rounded-2xl p-6 md:p-8 border border-primary-200 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                {data.ctaText}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base mb-5 max-w-lg mx-auto">
                Kliknij poniżej, aby przejść do <strong className="text-foreground-900">Kakobuy Spreadsheet</strong> i odkryj setki produktów w najlepszych cenach. Zacznij swoją przygodę z zakupami z Chin już dziś!
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Kakobuy Spreadsheet
                </a>
                <Link
                  to="/"
                  className="px-8 py-3.5 bg-white text-foreground-800 font-semibold text-sm rounded-full border border-background-300 hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Strona główna
                </Link>
              </div>
            </section>

            <div className="text-center pt-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Powrót do strony głównej
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}